<script setup>
import { computed, onBeforeMount, onUpdated, ref, watch } from 'vue';
import { SearchIcon } from '@imaios-js/icons/stroke24';
import Form from './treeViewSearchForm.vue';
import HistoricItem from './historicItem.vue';
import TreeViewSearchItemsList from './treeViewSearchList.vue';
import TreeViewSearchSkeleton from './treeViewSearchSkeleton.vue';
import { useItemListStore } from '../plugins/store/itemsList.store';
import { useHistoricStore } from '../plugins/store/historic.store';
import { pubsub } from '@assets/js/imaios-global/imaios-global';
import { fetchStructures, fetchSuggestions, NoResultsError } from '../structure-api.helper';

onBeforeMount(() => {
  useHistoricStore().initializeStore();
});

const props = defineProps({ taId: Number, isMobileMode: Boolean });

const itemListStore = useItemListStore();
const historicStore = useHistoricStore();

const searchValue = ref('');
const offset = ref(0);
const isDropdownOpen = ref(false);
const searchFormWrapper = ref(null);
const formWidth = ref();
const loading = ref();
const loadMore = ref();
const page = ref(1);
const result = ref([]);
const LIMIT_PAGE = 4;
const totalLastPage = ref(0);
const message = ref('');
const taId = computed(() => props.taId);
const isMobileMode = computed(() => props.isMobileMode);

watch(taId, async () => {
  page.value = 1;
  totalLastPage.value = 0;
  message.value = '';

  if (!searchValue.value) {
    return;
  }

  loading.value = true;
  await sendRequest();
  loading.value = false;
  openDropdown();
});

let debounce;
const onFormInputChange = async () => {
  clearTimeout(debounce);
  page.value = 1;
  totalLastPage.value = 0;
  message.value = '';

  if (!searchValue.value) {
    return;
  }

  loading.value = true;
  debounce = setTimeout(async () => {
    await sendRequest();
    loading.value = false;
  }, 500);
};

/**
 *  NOT fetching next page when:
 *   - reach the page limit
 *   - on input change, ignore scroll event when fetching first page
 *   - if the result of last page is fewer than 15 (limit result per request)
 * @returns {boolean}
 */
const shouldFetchNextPage = () => {
  return page.value < LIMIT_PAGE && !loading.value && totalLastPage.value >= 15;
};

const getScrollState = (el) => ({
  scrollTop: el.scrollTop,
  scrollHeight: el.scrollHeight,
  clientHeight: el.clientHeight
});

let scrollEndTimeout = null;
const handleScroll = async (e) => {
  if (scrollEndTimeout) {
    clearTimeout(scrollEndTimeout);
  }

  if (!shouldFetchNextPage()) {
    return;
  }

  scrollEndTimeout = setTimeout(async () => {
    const el = e.target;
    const { scrollTop, scrollHeight, clientHeight } = getScrollState(el);

    const nearBottom = scrollTop + clientHeight >= scrollHeight - 1;
    if (!nearBottom) {
      return;
    }

    page.value += 1;
    loadMore.value = true;

    if (displaySuggestions.value) {
      try {
        const newSuggestions = await fetchSuggestions(
          taId.value,
          searchValue.value,
          page.value
        );

        const newSuggestionsConverted = convertData(
          newSuggestions['suggestions']
        );
        totalLastPage.value = newSuggestionsConverted.length;
        result.value = [ ...result.value, ...newSuggestionsConverted ];
        itemListStore.setItems(result.value);
      }
      catch (e) {
        if (e instanceof NoResultsError) {
          totalLastPage.value = 0;
        }
        else {
          errorResponse.value = true;
          throw e;
        }
      }
      finally {
        loadMore.value = false;
      }
    }
    else {
      emptyResponse.value = errorResponse.value = false;

      try {
        const newResult = await fetchStructures(
          taId.value,
          searchValue.value,
          page.value
        );

        totalLastPage.value = newResult.length;
        result.value = [ ...result.value, ...newResult ];
        itemListStore.setItems(result.value);
      }
      catch (e) {
        if (e instanceof NoResultsError) {
          totalLastPage.value = 0;
        }
        else {
          errorResponse.value = true;
          throw e;
        }
      }
      finally {
        loadMore.value = false;
      }
    }
  }, 150);
};

const errorResponse = ref(false);
const emptyResponse = ref(false);
const displaySuggestions = ref(false);
const sendRequest = async () => {
  try {
    resetFetch();
    result.value = [];
    itemListStore.setItems(result.value);
    page.value = 1;

    if (!searchValue.value) {
      return;
    }

    const res = await fetchStructures(
      taId.value,
      searchValue.value,
      page.value
    );

    result.value = res;
    itemListStore.setItems(result.value);
    totalLastPage.value = result.value.length;
  }
  catch (e) {
    if (e instanceof NoResultsError) {
      try {
        const suggests = await fetchSuggestions(
          taId.value,
          searchValue.value,
          page.value
        );

        displaySuggestions.value = true;
        emptyResponse.value = true;
        result.value = convertData(suggests['suggestions']);
        itemListStore.setItems(result.value);
        totalLastPage.value = result.value.length;
      }
      catch (e2) {
        if (e2 instanceof NoResultsError) {
          displaySuggestions.value = false;
          emptyResponse.value = true;
          totalLastPage.value = 0;
        }
        else {
          errorResponse.value = true;
          throw e2;
        }
      }
    }
    else {
      errorResponse.value = true;
      throw e;
    }
  }
};

function resetFetch() {
  emptyResponse.value = false;
  errorResponse.value = false;
  displaySuggestions.value = false;
}

function convertData(suggestionsData) {
  let dataMapper = [];

  for (const item of suggestionsData) {
    const taMain = item['taMain'];
    const itemMain =
      item['suggest'].length === 1
        ? item['suggest'][0]
        : item['suggest'].find((i) => i.taId === taMain);

    const parentMap = {};
    item['suggest'].forEach((item) => {
      const { parentTitle, taId } = item;
      if (!parentMap[parentTitle]) {
        parentMap[parentTitle] = {
          title: parentTitle,
          tag: [],
          _items: {}
        };
      }
      parentMap[parentTitle].tag.push(taId);
      parentMap[parentTitle]._items[taId] = item;
    });

    itemMain['parentList'] = Object.values(parentMap).map((group) => {
      // Move taMain to front if it exists in the tag array
      const tagSorted = group.tag.sort((a, b) => {
        if (a === taMain) {
          return -1;
        }
        if (b === taMain) {
          return 1;
        }
        return 0;
      });

      // Add the match item (to Tag Id) -> to use in visitItem(item)
      const item = group._items[taMain] || group._items[tagSorted[0]];

      // Deconstruct origin item to avoid circular reference
      const { parentList, ...itemOrigin } = item;

      return {
        title: group.title,
        tag: tagSorted,
        item: itemOrigin
      };
    });

    // Move parentItem that contains taMain to front of parentList
    itemMain['parentList'].sort((a, b) => {
      const aHasMain = a.tag.includes(taMain);
      const bHasMain = b.tag.includes(taMain);

      return aHasMain === bHasMain ? 0 : aHasMain ? -1 : 1;
    });

    dataMapper.push(itemMain);
  }

  return dataMapper;
}

const openDropdown = () => (isDropdownOpen.value = true);
const closeDropdown = () => (isDropdownOpen.value = false);

const emit = defineEmits([ 'onFormFocus' ]);

const onFormFocus = () => {
  openDropdown();
  emit('onFormFocus');
};

const onFormClose = () => {
  resetSearch();
  closeDropdown();
};

const resetSearch = () => {
  searchValue.value = '';
  totalLastPage.value = 0;
  page.value = 1;
  message.value = '';
  result.value = [];
  itemListStore.setItems(result.value);
};

pubsub.subscribe(pubsub, 'closeSearchDropdown', onFormClose);

/**
 * cannot use onMounted()
 * bug? the clientWidth log out it different from reality dom
 * solution workaround: https://stackoverflow.com/questions/75797711/bug-on-getting-the-element-width-using-vue3-ref
 * onUpdated() detect dom's size changed too, so we don't need to subscribe to TREE_VIEW_NOTIFICATIONS.DRAGHOLDER_MOUSE_UP
 */
onUpdated(() => {
  formWidth.value = searchFormWrapper.value.clientWidth;
});

const showHistoric = computed(() => {
  {
    const historicByApplication =
      taId.value === 4
        ? historicStore.getSortedHistoricByApplication('vetanatomy')
        : historicStore.getSortedHistoricByApplication('eanatomy');
    return (
      searchValue.value.length === 0 && historicByApplication.value.length > 0
    );
  }
});

const shouldShowPadding = computed(() => {
  return (
    showHistoric.value ||
    loading.value ||
    emptyResponse.value ||
    errorResponse.value ||
    (result.value && result.value.length > 0)
  );
});
</script>

<template>
  <div style="position: relative" v-click-outside="closeDropdown">
    <div ref="searchFormWrapper">
      <Form id="tree-view-search-form"
            v-model="searchValue"
            @input="onFormInputChange"
            @close="onFormClose"
            @focus="onFormFocus"
      />
    </div>
    <div
      v-show="isDropdownOpen"
      id="search-dropdown"
      :class="{
        tree_view_search_result__Container: true,
        'mobile-structure': isMobileMode,
        paddingTopBottom: shouldShowPadding,
      }"
      :style="{ 'min-width': formWidth + 'px' }"
      @scroll="handleScroll"
    >
      <HistoricItem v-if="showHistoric" :current-ta-id="taId" />
      <template v-else-if="loading">
        <TreeViewSearchSkeleton :loading="loading" />
      </template>
      <template v-else>
        <div v-if="errorResponse" class="item-search structure-waiting">
          <div class="content-search">
            {{ $t('search.tree-view.error') }}
          </div>
        </div>
        <template v-else-if="emptyResponse">
          <div class="item-search structure-waiting">
            <SearchIcon />
            <div class="content-search">
              {{ $t('search.tree-view.no-result') }}
            </div>
          </div>
          <TreeViewSearchItemsList
            v-if="displaySuggestions"
            :items="result"
            :message-suggest="
              taId === 4
                ? $t('search.tree-view.suggest-in-human-terminologies')
                : $t('search.tree-view.suggest-in-other-terminologies')
            "
            :display-tag="true"
          />
          <TreeViewSearchSkeleton :loading="loadMore" />
        </template>
        <template v-else>
          <TreeViewSearchItemsList :items="result" :display-tag="false" />
          <TreeViewSearchSkeleton :loading="loadMore" />
        </template>
      </template>
    </div>
  </div>
</template>

<style lang="scss">
@use "/assets/css/theme/helpers/variables";
@use "/assets/css/theme/base/animations";

$mobileHeaderHeight: 110px;

.tree_view_search_result__Container.paddingTopBottom {
  padding: 0.5rem 0;
}

.tree_view_search_result__Container {
  display: flex;
  width: 550px;
  max-height: 350px;
  flex-direction: column;
  gap: 0.25rem;
  margin-top: 10px;
  align-items: flex-start;
  border-radius: 4px;
  background: variables.$dropdown-bg;
  color: variables.$dropdown-color;
  position: absolute;
  z-index: 10;
  overflow: hidden;
  overflow-y: auto;
  overscroll-behavior: none; /* Prevent parent scroll & disabled bounce/elastic scroll effects on MacOS */

  /* Light/default */
  box-shadow: 0 4px 24px 0 rgba(0, 0, 0, 0.08);

  .language-two {
    color: variables.$body-color;
    font-size: 13px;
  }

  .language-one {
    color: variables.$headings-color;
    font-size: 15px;
  }

  .item-search {
    padding: 0.5rem 1rem;
    align-items: center;
    display: flex;
    gap: 12px;
    width: 100%;

    .icon-container {
      flex: 0 0 28px;

      .icon-search {
        width: 28px;
        height: 28px;
        margin: auto;

        background-position: 50%;
        background-repeat: no-repeat;
        background-size: cover;
      }
    }

    .search-title-link {
      text-decoration: none;
    }

    .search-title {
      font-size: 15px;
      font-style: normal;
      line-height: normal;
    }

    .search-hierarchy a {
      font-size: 13px;
      font-style: normal;
      font-weight: 400;
      line-height: 18px; /* 138.462% */
      text-decoration: none;
      color: variables.$body-color;
    }
  }

  .item-search.structure-waiting {
    width: 550px;
    max-height: 400px;
    color: variables.$dropdown-color;
    font-size: 15px;
    font-style: italic;
    font-weight: 400;
    line-height: normal;
    align-self: stretch;
    background-color: variables.$dropdown-bg;
  }

  .item-search.active {
    background-color: variables.$dropdown-link-hover-bg;
  }

  .item-search.active .content-search .search-title {
    .language-two, .language-one {
      color: variables.$dropdown-link-hover-color;
    }
  }

  .item-search .content-search .search-hierarchy a {
    //position and z-index to override the stretched-link used on a parent
    position: relative;
    z-index: 2;
  }

  .item-search.active .content-search .search-hierarchy a {
    text-decoration: none;
  }

  .item-search .content-search .search-hierarchy a span:hover {
    background-color: variables.$layer-accent-selected-02 !important;
    text-decoration: underline;
    color: variables.$dropdown-link-hover-color;
  }
}

// Skeleton
// --------------------------------------------------
html[data-theme="light"] {
  --shimmer-background: #e3e3e3;
  --shimmer-forground: #efefef;
}

html[data-theme="dark"] {
  --shimmer-background: #4d4d4d;
  --shimmer-forground: #5b5b5b;
}

.loading {
  @include animations.useShimmerAnimation(
      1s,
      var(--shimmer-background, variables.$color-light-bg),
      var(--shimmer-forground, variables.$color-light-shimmer)
  );
}

.search-title .loading {
  width: 250px;
  height: 15px;
  margin-bottom: 8px;
}

.search-hierarchy .loading {
  width: 200px;
  height: 8px;
}

@media (max-width: 992px) {
  .tree_view_search_result__Container.mobile-structure {
    width: 100%;
    position: relative;
    max-height: calc(100vh - #{$mobileHeaderHeight});
    box-shadow: none;
    margin-top: 0;
  }
}
</style>
